import { Component, OnInit } from '@angular/core';
import pkg from '../../package.json';
import {
  defaultDirection,
  defaultLanguage,
  directionLocalStorageKey,
  languageLocalStorageKey,
} from '@fund-base/constants/locale/locale.constants';
import { PosthogService } from '@fund-base/services/posthog.service';
import { StorageService } from '@fund-base/services/storage/storage.service';
import { setDirection, setLanguage } from '@fund-base/store/actions/locale.actions';
import { setScreenSize } from '@fund-base/store/actions/ui.actions';
import { selectIsGlobalLoading, selectScreenSize } from '@fund-base/store/selectors/ui.selectors';
import { ScreenSize } from '@fund-base/types/ui/ui.types';
import { Store } from '@ngrx/store';
import { Observable, take, filter, combineLatest, takeUntil } from 'rxjs';
import { Destroyable } from './shared/components/destroyable/destroyable';
import { selectUser, selectVisitor } from './store/selectors/auth.selectors';
import { selectLanguage } from './store/selectors/locale.selectors';
import { NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends Destroyable implements OnInit {
  // global loading
  globalLoading$: Observable<boolean> = this.store.select(selectIsGlobalLoading);
  isVisitorUser: boolean;
  user$ = this.store.select(selectUser);
  visitor$ = this.store.select(selectVisitor);
  user: any;
  language$ = this.store.select(selectLanguage);
  language: string = 'en';

  constructor(
    private store: Store,
    private storageService: StorageService,
    private posthogService: PosthogService,
    private router: Router,
  ) {
    super();

    // cache
    this.invalidateCache();

    // language
    this.initLanguage();

    // direction
    this.initDirection();
  }

  invalidateCache() {
    const version = this.storageService.getItem('version');
    if (!version || version !== pkg.version) {
      this.storageService.clearAllStorage();
      this.storageService.setItem('version', pkg.version);
    }
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(event => {
        this.posthogService.capturePageviewIfEnabled(event.urlAfterRedirects);
      });

    // Identify once per user/visitor. Do NOT reset before identify — that minted a new
    // anonymous distinct_id on every store update and fragmented PostHog history.
    // Reset only when both user and visitor are cleared (logout).
    combineLatest([this.user$, this.visitor$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([user, visitor]) => {
        if (user) {
          const userProfile = {
            Name: user.first_name + ' ' + user.last_name,
            Email: user.email,
            Organization: user.organization?.organization_name,
            OrganizationOwner: user.organization?.owner_name,
            OrganizationOwnerEmail: user.organization?.owner_email,
            Status: 'User',
            Plan:
              user.organization && user.organization.membership && user.organization.membership.length > 0
                ? (user.organization.membership[0].plan?.name ?? 'Unknown')
                : 'Free',
            Phone: user.phone,
          };
          console.log('userProfile', userProfile);
          this.posthogService.identifyUser(`user-id-${user.id!}`, userProfile);
        } else if (visitor) {
          const visitorProfile = {
            Name: visitor.first_name + ' ' + visitor.last_name,
            Email: visitor.email,
            Organization: 'N/A',
            OrganizationOwner: 'N/A',
            OrganizationOwnerEmail: 'N/A',
            Status: 'Visitor',
            Plan: 'Free',
            Phone: visitor.phone,
          };
          console.log('visitorProfile', visitorProfile);
          this.posthogService.identifyUser(`visitor-id-${visitor.id!}`, visitorProfile);
        } else {
          this.posthogService.reset();
        }
      });

    this.language$.pipe(takeUntil(this.destroy$)).subscribe((language: string) => {
      this.language = language;
    });
  }

  initLanguage() {
    this.store.dispatch(
      setLanguage({
        language: this.storageService.getItem(languageLocalStorageKey) ?? defaultLanguage,
      }),
    );
  }

  initDirection() {
    this.store.dispatch(
      setDirection({
        direction: this.storageService.getItem(directionLocalStorageKey) ?? defaultDirection,
      }),
    );
  }

  onScreenSizedChanged(screenSize: ScreenSize) {
    this.store
      .select(selectScreenSize)
      .pipe(take(1))
      .subscribe(currentScreenSize => {
        if (currentScreenSize !== screenSize) {
          this.store.dispatch(
            setScreenSize({
              screenSize,
            }),
          );
        }
      });
  }
}
